import { Button, NumberInput, Radio, Stack, TextInput, Title } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";

import {
  getListTokensQueryKey,
  useCreateToken,
} from "../../api/generated/endpoints/identity/identity";
import type { TokenScope } from "../../api/generated/model";
import { TokenCreate } from "../../api/generated/zod";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { NewTokenSecret } from "./NewTokenSecret";

// `read` allows safe methods only; `write` is what lets a token change anything (A1 decision 13).
const ACCESS: Record<"read" | "write", TokenScope[]> = { read: ["read"], write: ["read", "write"] };

interface FieldErrors {
  name?: string;
  days?: string;
}

// The request field each form field fills.
const FIELD_OF: Record<string, keyof FieldErrors> = { name: "name", expires_in_days: "days" };

export function CreateToken({ teamId }: { teamId: string }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [access, setAccess] = useState<keyof typeof ACCESS>("read");
  const [days, setDays] = useState<number | string>(30);
  const [errors, setErrors] = useState<FieldErrors>({});
  const create = useCreateToken({
    // The list refetches; the secret never enters it.
    mutation: {
      onSuccess: () => queryClient.invalidateQueries({ queryKey: getListTokensQueryKey() }),
    },
  });

  if (create.data !== undefined) {
    const done = () => {
      create.reset();
      setName("");
    };
    return <NewTokenSecret created={create.data} onDone={done} />;
  }

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsed = TokenCreate.safeParse({
      name: name.trim(),
      scopes: ACCESS[access],
      expires_in_days: typeof days === "number" ? days : Number.NaN,
    });
    if (!parsed.success) {
      const found: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const field = FIELD_OF[String(issue.path[0])];
        if (field !== undefined) found[field] = MESSAGES[field];
      }
      setErrors(found);
      return;
    }
    setErrors({});
    create.mutate({ teamId, data: parsed.data });
  };

  return (
    <form onSubmit={submit} noValidate>
      <Stack gap="sm" maw={420}>
        <Title order={2} size="h4">
          New token
        </Title>
        <TextInput
          label="Name"
          description="Say what will use it, such as the agent or script."
          value={name}
          onChange={(event) => setName(event.currentTarget.value)}
          error={errors.name}
        />
        <Radio.Group
          label="Access"
          value={access}
          onChange={(value) => setAccess(value === "write" ? "write" : "read")}
        >
          <Stack gap="xs" mt="xs">
            <Radio value="read" label="Read only" />
            <Radio value="write" label="Read and write" />
          </Stack>
        </Radio.Group>
        <NumberInput
          label="Expires after (days)"
          min={1}
          max={90}
          allowDecimal={false}
          value={days}
          onChange={setDays}
          error={errors.days}
        />
        {create.isError ? <ErrorAlert error={create.error} /> : null}
        <Button type="submit" loading={create.isPending}>
          Create token
        </Button>
      </Stack>
    </form>
  );
}

const MESSAGES: Required<FieldErrors> = {
  name: "Give the token a name of up to 100 characters.",
  days: "Choose 1 to 90 days.",
};
